/**
 * Auth Store (Zustand)
 * Manages authentication state with real API calls
 */

import { create } from 'zustand';
import { authService } from '../../../shared/api/services/authService';
import { tokenStorage } from '../../../shared/api/client';
import { useAnalysisStore } from '../../../shared/lib/store/analysisStore';
import type { User } from '../../../entities/user';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  error: string | null;

  /** Initialize auth state from stored token */
  initialize: () => Promise<void>;
  /** Login with email + password */
  login: (email: string, password: string) => Promise<void>;
  /** Register a new user */
  register: (fullName: string, email: string, password: string) => Promise<void>;
  /** OAuth login stub (not yet implemented in backend) */
  loginWithProvider: (provider: 'google' | 'github') => Promise<void>;
  /** Logout and clear all tokens */
  logout: () => void;
  /** Clear error state */
  clearError: () => void;
}

export const useAuth = create<AuthState>()((set) => ({
  isAuthenticated: false,
  isLoading: true, // starts loading to check stored token
  user: null,
  error: null,

  initialize: async () => {
    const token = tokenStorage.getAccessToken();
    if (!token) {
      set({ isLoading: false, isAuthenticated: false });
      return;
    }

    try {
      const user = await authService.getCurrentUser();
      set({ isAuthenticated: true, user, isLoading: false });
    } catch {
      // Token is invalid or expired — clean up
      tokenStorage.clear();
      set({ isAuthenticated: false, user: null, isLoading: false });
    }
  },

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });

    try {
      const tokens = await authService.login(email, password);
      tokenStorage.setTokens(tokens.access_token, tokens.refresh_token);

      // Important: Reset local stores when a new user logs in to avoid data leakage
      useAnalysisStore.getState().resetStore();

      const user = await authService.getCurrentUser();
      set({ isAuthenticated: true, user, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      set({ isLoading: false, error: message });
      throw err;
    }
  },

  register: async (fullName: string, email: string, password: string) => {
    set({ isLoading: true, error: null });

    try {
      await authService.register({ email, password, full_name: fullName });

      // Auto-login after registration
      const tokens = await authService.login(email, password);
      tokenStorage.setTokens(tokens.access_token, tokens.refresh_token);

      const user = await authService.getCurrentUser();
      set({ isAuthenticated: true, user, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      set({ isLoading: false, error: message });
      throw err;
    }
  },

  loginWithProvider: async (provider: 'google' | 'github') => {
    // TODO: Implement OAuth flow when backend supports it
    console.warn(`OAuth login with ${provider} not yet implemented`);
    set({ error: `OAuth with ${provider} is not available yet` });
  },

  logout: () => {
    // Clear all storage
    tokenStorage.clear();
    useAnalysisStore.getState().resetStore();
    
    set({ isAuthenticated: false, user: null, error: null });
    
    // Hard refresh to clear React Query cache and memory state
    window.location.href = '/login';
  },

  clearError: () => set({ error: null }),
}));
